import { Model, Serializer } from "survey-core";
import type { SurveyCreator } from "survey-creator-react";

/**
 * Custom SurveyJS property to control element visibility based on view mode.
 * Applies to questions, panels, and pages.
 *
 * - Empty array []: Element appears in all views (default)
 * - ["main"]: Element appears only in the main report view
 * - ["condensed"]: Element appears only in the condensed view
 * - ["pdf"]: Element appears only in the PDF view
 * - Any combination: e.g., ["main", "condensed"] for main and condensed but not PDF
 */

let isRegistered = false;

const showInPropertyDefinition = {
  name: "showIn",
  displayName: "Show In",
  type: "multiplevalues",
  default: [],
  choices: ["main", "condensed", "pdf"],
  category: "general",
};

export function registerShowInProperty() {
  if (isRegistered) return;

  // Register for questions, panels, and pages
  Serializer.addProperty("question", showInPropertyDefinition);
  Serializer.addProperty("panel", showInPropertyDefinition);
  Serializer.addProperty("page", showInPropertyDefinition);

  isRegistered = true;
}

// CSS for hiding showIn elements (injected once)
const HIDDEN_CLASS = "showIn-hidden";
const VISIBILITY_STYLE_ID = "showIn-visibility-styles";

// Property names used to store showIn state on the survey model
const SHOW_IN_VIEW_MODE_KEY = "__showInViewMode";
const SHOW_IN_HIDDEN_NAMES_KEY = "__showInHiddenNames";
const SHOW_IN_HANDLERS_REGISTERED_KEY = "__showInHandlersRegistered";

function injectShowInVisibilityStyles() {
  if (typeof document === "undefined") return;

  // Check DOM for existing style (handles HMR/module reloads)
  if (document.getElementById(VISIBILITY_STYLE_ID)) return;

  const style = document.createElement("style");
  style.id = VISIBILITY_STYLE_ID;
  style.textContent = `
    .${HIDDEN_CLASS} {
      display: none !important;
    }
  `;
  document.head.appendChild(style);
}

/**
 * Hides the element via CSS class.
 */
function hideElement(htmlElement: HTMLElement) {
  htmlElement.classList.add(HIDDEN_CLASS);
}

/**
 * Recomputes and updates the parent row's visibility based on its children.
 * Called on every element render so the last element to render corrects
 * any premature row hiding from earlier siblings.
 */
function updateRowVisibility(htmlElement: HTMLElement) {
  const row = htmlElement.closest(".sd-row");
  if (!row) return;

  const questions = row.querySelectorAll(".sd-question, .sd-panel");
  const allHidden = Array.from(questions).every((q) =>
    q.classList.contains(HIDDEN_CLASS),
  );

  if (allHidden) {
    row.classList.add(HIDDEN_CLASS);
  } else {
    row.classList.remove(HIDDEN_CLASS);
  }
}

// Interface for SurveyJS elements we need to work with
interface ShowInElement {
  name: string;
  getPropertyValue: (name: string) => unknown;
}

/**
 * Checks if an element should be hidden based on its showIn property and current view mode.
 */
function shouldHideElement(element: ShowInElement, viewMode: string): boolean {
  const showIn = element.getPropertyValue("showIn") as string[] | undefined;

  // Empty array or undefined = visible in all views
  if (!showIn || showIn.length === 0) {
    return false;
  }

  return !showIn.includes(viewMode);
}

/**
 * Collects all element names that should be hidden for a given view mode.
 */
function collectHiddenElementNames(
  surveyModel: Model,
  viewMode: string,
): Set<string> {
  const hiddenElementNames = new Set<string>();

  const collectHiddenElements = (element: ShowInElement) => {
    if (shouldHideElement(element, viewMode)) {
      hiddenElementNames.add(element.name);
    }
  };

  // Scan all questions (including those in dynamic panel templates)
  surveyModel
    .getAllQuestions()
    .forEach((q) => collectHiddenElements(q as ShowInElement));
  surveyModel
    .getAllPanels()
    .forEach((p) => collectHiddenElements(p as unknown as ShowInElement));
  surveyModel.pages.forEach((p) => collectHiddenElements(p as ShowInElement));

  // Also scan paneldynamic templateElements for questions that will be cloned
  surveyModel.getAllQuestions().forEach((q) => {
    if (q.getType() === "paneldynamic") {
      const templateElements = q.getPropertyValue("templateElements") as
        | ShowInElement[]
        | undefined;
      if (templateElements) {
        templateElements.forEach(collectHiddenElements);
      }
    }
  });

  return hiddenElementNames;
}

/**
 * Applies view-specific visibility to elements based on their `showIn` property.
 * Uses CSS-based hiding to preserve default values (avoids clearInvisibleValues behavior).
 *
 * This function is idempotent - calling it multiple times with different viewModes
 * will update the behavior without registering duplicate handlers.
 *
 * @param surveyModel - The SurveyJS Model instance
 * @param viewMode - The current view mode: "main", "condensed", or "pdf"
 */
export function applyViewVisibility(
  surveyModel: Model,
  viewMode: "main" | "condensed" | "pdf",
) {
  injectShowInVisibilityStyles();

  // Store current viewMode and hidden names on the model so handlers can read dynamically
  const hiddenElementNames = collectHiddenElementNames(surveyModel, viewMode);
  surveyModel.setPropertyValue(SHOW_IN_VIEW_MODE_KEY, viewMode);
  surveyModel.setPropertyValue(SHOW_IN_HIDDEN_NAMES_KEY, hiddenElementNames);

  // Only register handlers once per survey model
  if (surveyModel.getPropertyValue(SHOW_IN_HANDLERS_REGISTERED_KEY)) {
    return;
  }
  surveyModel.setPropertyValue(SHOW_IN_HANDLERS_REGISTERED_KEY, true);

  /**
   * Apply visibility to rendered HTML element and update parent row.
   * Reads viewMode and hiddenNames from the model to avoid stale closures.
   * Always updates row visibility so later siblings can correct earlier decisions.
   */
  const applyVisibilityToElement = (
    element: ShowInElement,
    htmlElement: HTMLElement,
  ) => {
    // Read current values from model (not from closure)
    const currentViewMode = surveyModel.getPropertyValue(
      SHOW_IN_VIEW_MODE_KEY,
    ) as string;
    const currentHiddenNames = surveyModel.getPropertyValue(
      SHOW_IN_HIDDEN_NAMES_KEY,
    ) as Set<string>;

    if (
      shouldHideElement(element, currentViewMode) ||
      currentHiddenNames?.has(element.name)
    ) {
      hideElement(htmlElement);
    }

    // Always recompute row visibility - later siblings correct earlier decisions
    updateRowVisibility(htmlElement);
  };

  // Apply visibility when elements are rendered (catches initial render + dynamic panels)
  surveyModel.onAfterRenderQuestion.add((_, options) => {
    applyVisibilityToElement(
      options.question as ShowInElement,
      options.htmlElement,
    );
  });

  surveyModel.onAfterRenderPanel.add((_, options) => {
    applyVisibilityToElement(
      options.panel as unknown as ShowInElement,
      options.htmlElement,
    );
  });

  surveyModel.onAfterRenderPage.add((_, options) => {
    applyVisibilityToElement(
      options.page as ShowInElement,
      options.htmlElement,
    );
  });
}

/**
 * CSS styles for showIn visual indicators in the designer
 */
const showInIndicatorStyles = `
  .showIn-icon {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 18px;
    height: 18px;
    cursor: help;
    z-index: 9999;
    opacity: 0.7;
    transition: opacity 0.2s;
    background: rgba(255, 255, 255, 0.9);
    border-radius: 50%;
    padding: 2px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }

  .showIn-icon:hover {
    opacity: 1;
  }

  .showIn-icon svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  .showIn-icon--visible svg {
    fill: var(--success-light);
  }

  .showIn-icon--hidden svg {
    fill: var(--text-disabled);
  }

  .showIn-tooltip {
    position: absolute;
    top: 100%;
    right: 0;
    margin-top: 4px;
    padding: 6px 10px;
    background: #333;
    color: var(--common-white);
    font-size: 11px;
    border-radius: 4px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s, visibility 0.2s;
    z-index: 10000;
    pointer-events: none;
  }

  .showIn-tooltip::before {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 8px;
    border: 5px solid transparent;
    border-bottom-color: #333;
  }

  .showIn-icon:hover .showIn-tooltip {
    opacity: 1;
    visibility: visible;
  }

  .showIn-has-indicator {
    position: relative !important;
    overflow: visible !important;
  }

  /* Ensure parent containers don't clip the icon */
  .showIn-has-indicator.sd-element,
  .showIn-has-indicator.sd-question {
    overflow: visible !important;
  }
`;

// SVG for eye icon (visible)
const eyeVisibleSvg = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
</svg>`;

// SVG for eye icon with slash (hidden)
const eyeHiddenSvg = `<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/>
</svg>`;

const INDICATOR_STYLE_ID = "showIn-indicator-styles";

function injectShowInStyles() {
  if (typeof document === "undefined") return;

  // Check DOM for existing style (handles HMR/module reloads)
  if (document.getElementById(INDICATOR_STYLE_ID)) return;

  const styleElement = document.createElement("style");
  styleElement.id = INDICATOR_STYLE_ID;
  styleElement.textContent = showInIndicatorStyles;
  document.head.appendChild(styleElement);
}

function createShowInIndicator(
  showIn: string[],
  currentView: string = "main",
): HTMLElement {
  const isVisibleInCurrentView = showIn.includes(currentView);

  const container = document.createElement("div");
  container.className = `showIn-icon showIn-icon--${isVisibleInCurrentView ? "visible" : "hidden"}`;
  container.innerHTML = isVisibleInCurrentView ? eyeVisibleSvg : eyeHiddenSvg;

  // Create tooltip
  const tooltip = document.createElement("div");
  tooltip.className = "showIn-tooltip";
  tooltip.textContent = `Visible in: ${showIn.join(", ")}`;
  container.appendChild(tooltip);

  return container;
}

/**
 * Registers visual indicators for showIn property in the SurveyJS Creator.
 * Call this after creating the SurveyCreator instance.
 *
 * @param creator - The SurveyCreator instance
 * @param currentView - The current preview mode: "main" or "condensed"
 */
export function registerShowInVisualIndicators(
  creator: SurveyCreator,
  currentView: "main" | "condensed" = "main",
) {
  injectShowInStyles();

  creator.onSurveyInstanceCreated.add((_, options) => {
    // Support both old API (reason) and new API (area) for compatibility
    const isPreviewTab =
      options.area === "preview-tab" || options.reason === "test";
    if (!isPreviewTab) return;

    const survey = options.survey;

    const addIndicator = (
      element: { getPropertyValue: (name: string) => unknown },
      htmlElement: HTMLElement,
    ) => {
      const showIn = element.getPropertyValue("showIn") as string[];

      // Only show indicator if there are restrictions
      if (!showIn || !Array.isArray(showIn) || showIn.length === 0) {
        return;
      }

      // Target the question header/title area which is more stable than the main container
      const headerElement =
        htmlElement.querySelector(".sd-question__header") ||
        htmlElement.querySelector(".sd-panel__header") ||
        htmlElement.querySelector(".sd-page__title") ||
        htmlElement;

      const addIndicatorToElement = () => {
        // Don't add if indicator already exists
        if (htmlElement.querySelector(".showIn-icon")) return;

        htmlElement.classList.add("showIn-has-indicator");
        const indicator = createShowInIndicator(showIn, currentView);

        if (headerElement && headerElement !== htmlElement) {
          (headerElement as HTMLElement).style.position = "relative";
          headerElement.appendChild(indicator);
        } else {
          htmlElement.appendChild(indicator);
        }
      };

      // Add initially and after any sync re-renders
      addIndicatorToElement();
      requestAnimationFrame(addIndicatorToElement);
    };

    survey.onAfterRenderQuestion.add((_, opts) => {
      addIndicator(opts.question, opts.htmlElement);
    });

    survey.onAfterRenderPanel.add((_, opts) => {
      addIndicator(opts.panel, opts.htmlElement);
    });

    survey.onAfterRenderPage.add((_, opts) => {
      addIndicator(opts.page, opts.htmlElement);
    });
  });
}
